import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../theming/useTheme';
import {
  isLastLink,
  SecondaryLinksComponentProps,
  SecondaryLinksProps, SecondaryLinksThemeProps,
} from '../../components/navigation/header/secondaryHeader/SecondaryLinks';
import { maxVisibleLinksOnLargeAndXLarge } from '../../components/navigation/header/secondaryHeader/SecondaryHeader';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { AMPMoreLinks } from './AMPMoreLinks';
import { Span, semanticCSS } from '../../../typography/semanticTags';
import { getStyleBySize, FontSizesStyles } from '../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'secondaryLinks';

const getTextStyle = (baseUnit: BaseUnit, isActiveLink: boolean, lastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      fontWeight: isActiveLink ? 600 : 400,
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0,
      },
    },
  });
};

export const getContainerStyle = (baseUnit: BaseUnit) => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      gridColumnGap: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        overflowX: 'auto',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        overflowX: 'auto',
      },
    },
  });
};

const getCreateLinkFunction = (
  idleColor: string,
  activeStateColor: string,
  hoverStateColor: string,
  baseUnit: BaseUnit,
  linksLength: number,
  pagePath: string,
  fontSizesStyles: FontSizesStyles,
) => {
  const createLink = (link: HeaderLinksDataProps, index: number) => {
    const { href, text } = link;
    const isActiveLink = href === pagePath;

    const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1
      ? {
        [MEDIA_BREAKPOINTS.LARGE]: {
          display: 'none',
        },
        [MEDIA_BREAKPOINTS.SMALL]: {
          display: 'inline-flex',
        },
        [MEDIA_BREAKPOINTS.MEDIUM]: {
          display: 'inline-flex',
        },
      }
      : {};
    const linkStyle = generateLinkStyleFromTheme({
      idleColor: isActiveLink ? activeStateColor : idleColor,
      activeStateColor,
      hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor,
    }, linkStyleBasedOnScreen);
    const style = getTextStyle(baseUnit, isActiveLink, isLastLink(index, linksLength));
    const fontsSizesForActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
    const fontsSizesForNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
    const contentStyle = getStyleBySize(fontSizesStyles, isActiveLink ? fontsSizesForActive : fontsSizesForNotActive);
    return (
      <TextLinkComponent key={index} href={href} linkStyle={linkStyle}>
        <Span styles={semanticCSS(contentStyle, style.link)}>{text}</Span>
      </TextLinkComponent>
    );
  };
  return createLink;
};

export const AMPSecondaryLinksComponent: React.FunctionComponent<SecondaryLinksComponentProps> = props => {
  const { idleColor, moreLinksText, baseUnit, activeStateColor, hoverStateColor, links, pagePath, fontSizesStyles } = props;
  const moreLinks = links.length > maxVisibleLinksOnLargeAndXLarge ? links.slice(maxVisibleLinksOnLargeAndXLarge - 1) : [];
  const style = getContainerStyle(baseUnit);
  return (
    <React.Fragment>
      <div className={css(style.container)}>
        {links.map(getCreateLinkFunction(idleColor, activeStateColor, hoverStateColor, baseUnit, links.length, pagePath, fontSizesStyles))}
      </div>
      {moreLinks.length > 0 ? <AMPMoreLinks links={moreLinks} moreLinksText={moreLinksText} pagePath={pagePath} /> : null}
    </React.Fragment>
  );
};

const mapThemeProps = (theme: SecondaryLinksThemeProps) => {
  return {
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
    hoverStateColor: theme.hoverStateColor,
    baseUnit: theme.baseUnit,
    hoverOtherLinksColors: theme.hoverOtherLinksColors,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const AMPSecondaryLinks = (props: SecondaryLinksProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <AMPSecondaryLinksComponent {...themeContext} {...props} />;
};
